import React, { Component } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Tooltip from '@mui/material/Tooltip';
import { AgCharts } from 'ag-charts-community';

function triggerDownload(href, fileName) {
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function escapeCsv(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build CSV from AG Charts options.data + series yKeys (Apex-style data export).
 */
export function buildChartCsv(options) {
  const data = (options && options.data) || [];
  const series = (options && options.series) || [];
  if (!data.length) return '';

  const xKey =
    (series[0] && (series[0].xKey || series[0].angleKey || series[0].calloutLabelKey)) ||
    Object.keys(data[0] || {})[0];

  const yCols = series
    .map((s) => ({
      key: s.yKey || s.angleKey || s.valueKey,
      name: s.yName || s.legendItemKey || s.calloutLabelKey || s.yKey || s.angleKey || 'value',
    }))
    .filter((c) => c.key);

  // Pie / donut: one value column
  if (!yCols.length && series[0] && series[0].type === 'pie') {
    const angleKey = series[0].angleKey || 'value';
    const labelKey = series[0].legendItemKey || series[0].calloutLabelKey || 'name';
    const header = [labelKey, angleKey].join(',');
    const lines = data.map((row) =>
      [escapeCsv(row[labelKey]), escapeCsv(row[angleKey])].join(',')
    );
    return [header, ...lines].join('\n');
  }

  const headers = [xKey || 'category', ...yCols.map((c) => c.name)];
  const lines = data.map((row) => {
    const cells = [escapeCsv(row[xKey])];
    yCols.forEach((c) => cells.push(escapeCsv(row[c.key])));
    return cells.join(',');
  });
  return [headers.join(','), ...lines].join('\n');
}

/**
 * Hamburger menu matching ApexCharts toolbar: Download SVG / PNG / CSV.
 */
class ChartExportMenu extends Component {
  state = { anchorEl: null };

  openMenu = (e) => {
    this.setState({ anchorEl: e.currentTarget });
  };

  closeMenu = () => {
    this.setState({ anchorEl: null });
  };

  getFileBase = () => {
    const { fileName } = this.props;
    const base = (fileName || 'chart').replace(/[^\w-]+/g, '_');
    return base;
  };

  getChart = () => {
    const { chartRef } = this.props;
    if (!chartRef) return null;
    if (typeof chartRef.getChart === 'function') return chartRef.getChart();
    if (chartRef.current && typeof chartRef.current.getChart === 'function') {
      return chartRef.current.getChart();
    }
    return chartRef.current || chartRef.chart || null;
  };

  downloadPng = () => {
    this.closeMenu();
    const chart = this.getChart();
    if (!chart) return;
    try {
      AgCharts.download(chart, {
        fileName: this.getFileBase(),
        fileFormat: 'image/png',
      });
    } catch (err) {
      console.error('[ChartExport] PNG', err);
    }
  };

  downloadSvg = async () => {
    this.closeMenu();
    const chart = this.getChart();
    if (!chart) return;
    try {
      // Community charts render to canvas — wrap PNG in an SVG for Apex parity.
      const dataUrl = await AgCharts.getImageDataURL(chart, {
        fileFormat: 'image/png',
      });
      const opts = chart.getOptions ? chart.getOptions() : {};
      const containerWidth =
        opts.container && opts.container.clientWidth
          ? opts.container.clientWidth
          : null;
      const width = opts.width || containerWidth || 800;
      const height = opts.height || 320;
      const svg = `<?xml version="1.0" encoding="UTF-8"?>\n<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><image width="${width}" height="${height}" xlink:href="${dataUrl}" href="${dataUrl}"/></svg>`;
      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `${this.getFileBase()}.svg`);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('[ChartExport] SVG', err);
    }
  };

  downloadCsv = () => {
    this.closeMenu();
    const { options, csvData } = this.props;
    const csv =
      typeof csvData === 'string'
        ? csvData
        : buildChartCsv(options || (this.getChart() && this.getChart().getOptions && this.getChart().getOptions()));
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${this.getFileBase()}.csv`);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  render() {
    const { anchorEl } = this.state;
    const { disabled } = this.props;

    return (
      <React.Fragment>
        <Tooltip title="Download chart">
          <span>
            <IconButton
              size="small"
              onClick={this.openMenu}
              disabled={disabled}
              aria-label="Download chart"
              sx={{
                color: '#64748b',
                backgroundColor: 'rgba(255,255,255,0.9)',
                border: '1px solid #e2e8f0',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={this.closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={this.downloadSvg} dense>
            <ListItemText primary="Download SVG" />
          </MenuItem>
          <MenuItem onClick={this.downloadPng} dense>
            <ListItemText primary="Download PNG" />
          </MenuItem>
          <MenuItem onClick={this.downloadCsv} dense>
            <ListItemText primary="Download CSV" />
          </MenuItem>
        </Menu>
      </React.Fragment>
    );
  }
}

ChartExportMenu.propTypes = {
  chartRef: PropTypes.any,
  fileName: PropTypes.string,
  options: PropTypes.object,
  csvData: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ChartExportMenu;



// WEBPACK FOOTER //
// ./src/components/dashboard2/ChartExportMenu.js